import type {
  IrregularVerb,
  Level,
  VerbAcceptedForms,
  VerbDistractorPair,
  VerbDistractors,
  VerbExample,
} from '../types';

function cleanString(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function isLevel(value: unknown): value is Level {
  return value === 'A1' || value === 'A2' || value === 'B1' || value === 'B2';
}

function toStringArray(value: unknown) {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => (typeof item === 'string' ? item.split(',') : []))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeAcceptedForms(value: unknown): VerbAcceptedForms {
  if (!value || typeof value !== 'object') {
    return { pastSimple: [], pastParticiple: [] };
  }

  const source = value as Record<string, unknown>;

  return {
    pastSimple: toStringArray(source.pastSimple),
    pastParticiple: toStringArray(source.pastParticiple),
  };
}

function normalizeDistractorPair(value: unknown): VerbDistractorPair | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const source = value as Record<string, unknown>;
  const pastSimple = cleanString(source.pastSimple);
  const pastParticiple = cleanString(source.pastParticiple);

  if (!pastSimple || !pastParticiple) {
    return null;
  }

  return { pastSimple, pastParticiple };
}

function normalizeDistractors(value: unknown): VerbDistractors {
  if (!value || typeof value !== 'object') {
    return { pastSimple: [], pastParticiple: [], wrongPairs: [] };
  }

  const source = value as Record<string, unknown>;

  return {
    pastSimple: toStringArray(source.pastSimple),
    pastParticiple: toStringArray(source.pastParticiple),
    wrongPairs: Array.isArray(source.wrongPairs)
      ? source.wrongPairs
          .map((item) => normalizeDistractorPair(item))
          .filter((item): item is VerbDistractorPair => Boolean(item))
      : [],
  };
}

function normalizeExample(example: unknown): VerbExample | null {
  if (!example || typeof example !== 'object') {
    return null;
  }

  const source = example as Record<string, unknown>;
  const en = cleanString(source.en ?? source.english);
  const uk = cleanString(source.uk ?? source.ukrainian);

  if (!en && !uk) {
    return null;
  }

  return {
    en,
    uk,
    english: en,
    ukrainian: uk,
  };
}

export function extractVerbLevel(tags: string[], fallback?: unknown): Level {
  if (isLevel(fallback)) {
    return fallback;
  }

  if (tags.includes('A1')) {
    return 'A1';
  }

  if (tags.includes('A2')) {
    return 'A2';
  }

  if (tags.includes('B1')) {
    return 'B1';
  }

  return 'B2';
}

export function normalizeVerb(value: Partial<IrregularVerb> & Record<string, unknown>): IrregularVerb {
  const tags = toStringArray(value.tags);
  const examplesSource = Array.isArray(value.examples)
    ? value.examples
    : value.example
      ? [value.example]
      : [];
  const examples = examplesSource
    .map((example) => normalizeExample(example))
    .filter((example): example is VerbExample => Boolean(example));

  const base = cleanString(value.base ?? value.infinitive);
  const past = cleanString(value.past ?? value.pastSimple);
  const pp = cleanString(value.pp ?? value.pastParticiple);
  const translation = cleanString(value.translation ?? value.uk);
  const level = extractVerbLevel(tags, value.level);
  const firstExample = examples[0];
  const accepted = normalizeAcceptedForms(value.accepted);
  const distractors = normalizeDistractors(value.distractors);

  return {
    id: cleanString(value.id) || base || 'verb',
    base,
    past,
    pp,
    uk: translation,
    tags,
    ipa: cleanString(value.ipa) || undefined,
    examples,
    accepted: {
      pastSimple: accepted.pastSimple.length > 0 ? accepted.pastSimple : splitVerbFormAlternatives(past),
      pastParticiple: accepted.pastParticiple.length > 0 ? accepted.pastParticiple : splitVerbFormAlternatives(pp),
    },
    distractors,
    infinitive: cleanString(value.infinitive) || base,
    pastSimple: cleanString(value.pastSimple) || past,
    pastParticiple: cleanString(value.pastParticiple) || pp,
    translation,
    level,
    example: firstExample
      ? {
          english: firstExample.english ?? firstExample.en,
          ukrainian: firstExample.ukrainian ?? firstExample.uk,
        }
      : undefined,
  };
}

export function normalizeVerbForm(value: string) {
  return value.toLowerCase().trim().replace(/\s+/g, ' ');
}

export function splitVerbFormAlternatives(value: string | string[]) {
  const source = Array.isArray(value) ? value : [value];

  return Array.from(
    new Set(
      source.flatMap((item) => {
        const normalized = normalizeVerbForm(item);

        return [normalized]
          .concat(normalized.split('/'))
          .flatMap((part) => part.split(','))
          .flatMap((part) => part.split(/\s+or\s+/))
          .map((part) => part.trim())
          .filter(Boolean);
      }),
    ),
  );
}

export function matchesVerbForm(input: string, expected: string | string[]) {
  const normalizedInput = normalizeVerbForm(input);

  if (!normalizedInput) {
    return false;
  }

  return splitVerbFormAlternatives(expected).includes(normalizedInput);
}
